import * as createMigration from 'redux-persist-migrate';
import * as localForage from 'localforage';

import { AnyAction, applyMiddleware, compose, createStore } from 'redux';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { OfflineAction } from '@redux-offline/redux-offline/lib/types';
import rootReducer, { IinitialState } from '../reducers';
import thunk, { ThunkMiddleware } from 'redux-thunk';
import { toastr } from 'react-redux-toastr';
import { PersistorConfig } from 'redux-persist';
import SentryMiddleware from './SentryMiddleware';
import { markerAfterStoring, sortMaterialsOldestFirst } from './materialCatchup.helpers';
import { constants } from '../constants/constants';
import { asyncProcessingMiddleware } from '../utils/asyncProcessingMiddleware';
import { detectNetwork } from './detectNetwork';
import initialState from '../reducers/initialState';
import { msalFetch } from '../components/auth/Auth-Utils';
import { offline } from '@redux-offline/redux-offline';
import offlineConfig from '@redux-offline/redux-offline/lib/defaults/index';
import { rootMigrations } from './migrations/rootMigrations';
import { store } from '../index';
import { setSyncStatus, setIsDownloadingJob } from '../actions/commonActions';
import * as types from '../actions/actionTypes';
import { clearSelectedJob, clearSelectedWorkOrderID } from '../actions/workOrderActions';
import { getComplianceCheckListForJob } from '../actions/complianceCheckListActions';
import { IMaterial, ImeasurementPointResult, Ipart, Iproduct, Isubcategory } from '../models';
import { buildOpenRepairConflictPayload, isOpenRepairConflict } from './openRepairConflict.helpers';
import { getMostRecentDate } from '../utils/dateUtils';
import { materialToProduct, materialToPart, productToMaterial } from '../utils/materialMapper';
import { appendSignatureFields } from './signatureFormData.helpers';

// Track processed action IDs to prevent duplicate outbox entries
const processedActionIds = new Set<string>();

// Cleanup processed action IDs periodically to prevent memory leaks
setInterval(() => {
    if (processedActionIds.size > 1000) {
        console.log(
            `[processedActionIds]: Cleaning up ${processedActionIds.size} processed action IDs to prevent memory leak`
        );
        processedActionIds.clear();
    }
}, 300000); // Clean up every 5 minutes if size exceeds 1000

export const materialStore = localForage.createInstance({
    name: 'my-med-gas-mobile',
    storeName: 'materials'
});

/**
 * Detects legacy 'products' and 'parts' object stores in IndexedDB.
 * If found, deletes the entire database and resets sync keys so the next
 * sync performs a full download into the unified 'materials' store.
 */
export const migrateToMaterialsStore = (): Promise<boolean> => {
    return new Promise(resolve => {
        const request = indexedDB.open('my-med-gas-mobile');
        request.onsuccess = () => {
            const db = request.result;
            const storeNames = Array.from(db.objectStoreNames);
            const hasOldStores = storeNames.includes('products') || storeNames.includes('parts');
            db.close();

            if (hasOldStores) {
                console.log('[migrateToMaterialsStore]: Legacy products/parts stores detected, deleting database');
                const deleteRequest = indexedDB.deleteDatabase('my-med-gas-mobile');
                deleteRequest.onsuccess = async () => {
                    await localForage.removeItem('med-gas-mobile_all-products_delta');
                    await localForage.removeItem('med-gas-mobile_all-products_full_sync');
                    console.log('[migrateToMaterialsStore]: Migration complete — full re-download will occur');
                    resolve(true);
                };
                deleteRequest.onerror = () => {
                    console.error('[migrateToMaterialsStore]: Failed to delete database');
                    resolve(false);
                };
                deleteRequest.onblocked = () => {
                    console.warn('[migrateToMaterialsStore]: Database delete blocked by open connections');
                    resolve(false);
                };
            } else {
                resolve(false);
            }
        };
        request.onerror = () => {
            console.error('[migrateToMaterialsStore]: Failed to open database for migration check');
            resolve(false);
        };
    });
};

export const materialStoreMigrationComplete = migrateToMaterialsStore();

const handleCriticalOutboxError = (showToast = true, action: OfflineAction) => {
    console.error('[handleCriticalOutboxError]: critical outbox error', action);
    if (showToast) {
        document.dispatchEvent(new CustomEvent('criticalOutboxErrorWithToast'));
    } else {
        document.dispatchEvent(new CustomEvent('criticalOutboxError'));
    }
};

/**
 * MMG-4336: the API rejects inspection answers for assets with an open repair (409) but still saves
 * the rest of the batch. Keep the rejected answers in local state so the user can retry them with
 * the override, and surface the conflict for the block modal. Returns false when the response
 * carries no conflicts, so the caller falls back to normal error handling.
 */
const handleOpenRepairConflict = (err: AxiosError, action: any): boolean => {
    const batch = (action?.meta?.offline?.effect?.axiosOptions?.data || []) as ImeasurementPointResult[];
    const payload = buildOpenRepairConflictPayload(err.response?.data, batch);

    if (!payload) {
        return false;
    }

    // redux-offline reads meta.offline.rollback AFTER this catch (send.js), so emptying it here keeps
    // every answer locally: accepted ones are already saved, blocked ones are needed for the retry.
    const rollback = action?.meta?.offline?.rollback;
    if (rollback) {
        rollback.results = [];
    }

    store.dispatch({ type: types.SET_OPEN_REPAIR_CONFLICTS, ...payload });

    return true;
};

const effect = async (
    { axiosOptions, message }: { axiosOptions: AxiosRequestConfig; message: string },
    action: any
) => {
    const url = axiosOptions?.url || ''; // if this is an empty string, it will generate a 404 response and be discarded.

    if (
        !axiosOptions.data ||
        (typeof axiosOptions.data === 'object' && Object.keys(axiosOptions.data).length === 0) ||
        (Array.isArray(axiosOptions.data) && axiosOptions.data.length === 0)
    ) {
        if (action && action.type && action.type === types.ADD_JOB_SIGNATURE && action.signature) {
            let signature = action.signature;
            let signatureBlob = signature.signature;

            // If signature blob is not present or is an empty object, try to load it from localForage
            if (!signatureBlob || (typeof signatureBlob === 'object' && Object.keys(signatureBlob).length === 0)) {
                const storedSignature = await localForage.getItem<string | Blob>(signature.id);
                if (storedSignature) {
                    signatureBlob = storedSignature;
                }
            }

            // Create FormData and append the signature blob first
            const formData = new FormData();
            if (signatureBlob instanceof Blob) {
                formData.append('Signature', signatureBlob);
            } else if (typeof signatureBlob === 'string') {
                // Convert base64/data URL to Blob
                const signatureBlobParts = signatureBlob.split(',');

                const bytes = signatureBlobParts[1] || '';

                const byteString = atob(bytes);
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }
                const blob = new Blob([ab], { type: 'image/png' });
                formData.append('Signature', blob);
            }

            axiosOptions.data = appendSignatureFields(formData, signature);
        }
    }

    return msalFetch(url, axiosOptions)
        .then((response: any) => {
            // Manually dispatch the commit action for cloning operations
            // This ensures the getLatest with cloning=true is called after the API succeeds
            if (action?.isCloning && url.includes('inventory/add') && action.meta?.offline?.commit) {
                store.dispatch(action.meta.offline.commit);
            }

            /*
             * Linking work orders makes the server seed this job's carried-over deficiencies, so the
             * checklist on screen is stale the moment the call lands. The refresh has to hang off the
             * queue rather than the action: the action only enqueues, so firing it there would race
             * the very request the new rows come from, and would never fire at all for a link made
             * offline and flushed later.
             */
            if (url.toLowerCase().includes('/job/addworkorders')) {
                const linkedJobID = (axiosOptions.data as any)?.id;
                if (linkedJobID) {
                    store.dispatch(getComplianceCheckListForJob(linkedJobID) as any);
                }
            }

            // Clean up processed action ID on successful completion
            const actionId = (action as any)?.actionId;
            if (actionId && processedActionIds.has(actionId)) {
                processedActionIds.delete(actionId);
                console.log(`[effect]: Cleaned up processed action ID ${actionId} after successful completion`);
            }
            return response;
        })
        .catch((err: AxiosError) => {
            // If there is an error, set the sync status to false
            if (store.getState().syncManager.isSyncRunning === true) {
                store.dispatch(setSyncStatus(false));
                store.dispatch(
                    toastr.warning(
                        'Warning', // ('common:warning'), // Need to figure out how to get the t function here
                        'Sync was unable to complete. Please find a better internet connection and try again.',
                        constants.toastrWarning
                    )
                );
            }

            // Are we currently downloading a job?
            // Check if one of the required API calls failed
            // if so, take the user back to the job screen and tell them to try again with better internet
            // TODO: this download job needs to be refactored, we shouldn't navigate to /devices if we don't have the job downloaded yet...
            if (store.getState().syncManager.downloadingJob === true && action && action.type) {
                const actionType = action.type;
                if (actionType === types.DOWNLOAD_JOB) {
                    store.dispatch(setIsDownloadingJob(false));
                    store.dispatch(clearSelectedWorkOrderID());
                    store.dispatch(clearSelectedJob());
                    store.dispatch(
                        toastr.warning(
                            'Warning', // ('common:warning'), // Need to figure out how to get the t function here
                            'Downloading job was unable to complete. Please find a better internet connection and try again.',
                            constants.toastrWarning
                        )
                    );
                    window.location.href = '/jobs';
                }
            }

            // the open repair block reports its own modal, so it must not raise the generic save toast
            const openRepairHandled = isOpenRepairConflict(err, url) && handleOpenRepairConflict(err, action);

            // If it's a network related error, like a timeout, don't show a toast to the user, just let the app keep retrying
            if (
                err !== undefined &&
                err.code &&
                (err.code === 'ECONNABORTED' || err.code.trim() === 'Error: Network Error')
            ) {
                console.error('[msalFetch Network Error]:', message, err);
                throw err;
            } else if (err !== undefined && err.message && err.message.trim() === 'Network Error') {
                console.error('[msalFetch Network Error]:', message, err);
                throw err;
            } else if (openRepairHandled) {
                console.warn('[msalFetch]: open repair block', message);
                throw err;
            } else if (err !== undefined && err.response && err.response.status >= 500) {
                constants.handleError(err, message, 'error');
            } else {
                console.error('[msalFetch]:', message, err);
                constants.handleError(err, message);
                throw err;
            }
        });
};

// if discard returns false, then it will try again
const discard = (error: AxiosError, action: OfflineAction, retries: number) => {
    const { request, response } = error;

    console.log('discard', error, action, retries);

    // Check if this action has an actionId and if we've already processed it
    const actionId = (action as any)?.actionId;
    if (actionId && processedActionIds.has(actionId)) {
        console.log(
            `[discard]: Duplicate action detected with ID ${actionId}, discarding to prevent duplicate sync queue entry`
        );
        return true; // Discard duplicate actions
    }

    let requestType = '';

    if (action && action.meta && action.meta.offline && action.meta.offline.effect) {
        // object "effect" has type object, so TS is not happy
        const { axiosOptions } = action.meta.offline.effect as any;
        if (axiosOptions && axiosOptions.method) {
            requestType = axiosOptions.method.toLowerCase().trim();
        }
    }

    if (!request && (requestType === 'post' || requestType === 'put')) {
        // If the user is offline or in a state where they are trying to come back online, don't discard any POST or PUT requests, we don't want to lose any data
        // Once they have a stable connection again, and token, "request" shouldn't be undefined anymore

        // Track this action ID to prevent future duplicates
        if (actionId) {
            processedActionIds.add(actionId);
        }

        return false;
    }

    if (!request) {
        throw error;
    } // There was an error creating the request
    if (!response) {
        // Track this action ID to prevent future duplicates
        if (actionId) {
            processedActionIds.add(actionId);
        }
        return false;
    } // There was no response
    if (response.status === 401) {
        return false; // unauthorized
    }

    // If it's a Bad Request, then the app sent an incorrect payload, so we should discard it
    if (response.status >= 400 && response.status < 500) {
        return true;
    }

    // if it is a 500, send it up to the AppLog table
    if (response.status >= 500) {
        handleCriticalOutboxError(false, action);
        return true;
    } else {
        return false;
    }
    //  IDEA: if retryScheduled true and online true, we could show a banner with a button to discard the request.
};

const retry = (action: OfflineAction, retries: number): number | null => {
    return 60000; // retry in 1 minute
};

export const saveMaterials = async (materials: IMaterial[]) => {
    let updateDateStr: string | null = await localForage.getItem('med-gas-mobile_all-products_delta');
    let mostRecentDate = updateDateStr ? new Date(updateDateStr) : new Date(0);

    for (const material of materials) {
        if (material.date) {
            const materialDate = new Date(material.date);
            if (materialDate > mostRecentDate) {
                mostRecentDate = materialDate;
            }
        }
        await materialStore.setItem(material.id, material);
    }

    await localForage.setItem('med-gas-mobile_all-products_delta', mostRecentDate.toISOString());
};

export const saveMaterialsWithProgressTracking = async (
    materials: IMaterial[],
    onProgress?: (processed: number, total: number) => void
) => {
    const updateDateStr: string | undefined | null = await localForage.getItem('med-gas-mobile_all-products_delta');
    let marker: Date | null = updateDateStr ? new Date(updateDateStr) : null;

    // MMG-4461: oldest first, marker saved after every group - a page that dies mid-way asks for less next time
    const sorted = sortMaterialsOldestFirst(materials);
    const total = sorted.length;
    const batchSize = 50;

    for (let i = 0; i < total; i += batchSize) {
        const batch = sorted.slice(i, i + batchSize);

        for (const material of batch) {
            await materialStore.setItem(material.id, material);
        }

        const processed = Math.min(i + batchSize, total);
        const advanced = markerAfterStoring(sorted, processed, marker);
        if (advanced) {
            marker = advanced;
            await localForage.setItem('med-gas-mobile_all-products_delta', marker.toISOString());
        }

        if (onProgress) {
            onProgress(processed, total);
        }

        if (batch.length >= batchSize && processed < total) {
            await new Promise(resolve => setTimeout(resolve, 10));
        }
    }

    await localForage.setItem('med-gas-mobile_all-products_delta', (marker || new Date(0)).toISOString());
};

export const saveProducts = async (products: Iproduct[]) => {
    let updateDateStr: string | null = await localForage.getItem('med-gas-mobile_all-products_delta');
    let mostRecentDate = updateDateStr ? new Date(updateDateStr) : new Date(0);

    for (const product of products) {
        const newDate = getMostRecentDate(product, mostRecentDate);
        if (newDate) {
            mostRecentDate = newDate;
        }

        await materialStore.setItem(product.id, productToMaterial(product));
    }

    await localForage.setItem('med-gas-mobile_all-products_delta', mostRecentDate.toISOString());
};

export const saveProduct = async (product: Iproduct) => {
    let updateDateStr: string | undefined | null = await localForage.getItem('med-gas-mobile_all-products_delta');
    let mostRecentDate = updateDateStr ? new Date(updateDateStr) : new Date(0);

    const newDate = getMostRecentDate(product, mostRecentDate);
    if (newDate) {
        mostRecentDate = newDate;
    }

    await materialStore.setItem(product.id, productToMaterial(product));
    await localForage.setItem('med-gas-mobile_all-products_delta', mostRecentDate.toISOString());
};

export const getProductByID = async (productID: string): Promise<Iproduct | null> => {
    const material: IMaterial | null = await materialStore.getItem(productID);
    if (!material || material.type !== 1) return null;
    return materialToProduct(material);
};

export const getProductsByIDs = async (productIDs: string[]) => {
    const products: { [key: string]: Iproduct } = {};

    for (const productID of productIDs) {
        const product = await getProductByID(productID);
        if (product) {
            products[productID] = product as Iproduct;
        }
    }

    return products;
};

export const getMaterialByID = async (materialID: string): Promise<IMaterial | null> => {
    return await materialStore.getItem(materialID);
};

export const getAllProductsFromStore = async () => {
    const products: { [key: string]: Iproduct } = {};

    await materialStore.iterate((value: IMaterial, key) => {
        if (value.type === 1) {
            products[key] = materialToProduct(value);
        }
    });

    return products || {};
};

export const getAllPartsFromStore = async () => {
    const parts: { [key: string]: Ipart } = {};

    await materialStore.iterate((value: IMaterial, key) => {
        parts[key] = materialToPart(value);
    });

    return parts || {};
};

export const getFilteredProductsFromStore = async ({
    subcategoryID,
    mainCategoryID,
    searchString,
    brandID,
    powerID,
    productTypeID,
    systemSizeID,
    isFinalProduct,
    productStandards,
    subcategories,
    pageSize = constants.searchProductPageCount
}: {
    subcategoryID?: string;
    mainCategoryID?: string;
    searchString?: string;
    brandID?: string;
    powerID?: string;
    productTypeID?: string;
    systemSizeID?: string;
    isFinalProduct?: boolean;
    productStandards?: string[];
    subcategories?: { [key: string]: Isubcategory };
    pageSize?: number;
}) => {
    const filteredProducts: Iproduct[] = [];

    await materialStore.iterate((material: IMaterial, key: string) => {
        if (material.type !== 1) return;
        if (!material.name) return;

        const prod = materialToProduct(material);

        const subcategory = subcategories?.[prod.subcategoryID];
        if (subcategoryID && prod.subcategoryID !== subcategoryID) return;
        if (mainCategoryID && subcategory?.mainCategoryID !== mainCategoryID) return;

        const nameParts: string[] = [prod.name, prod.description];
        if (prod?.sapMaterialNumber) nameParts.push(prod.sapMaterialNumber);
        if (prod?.sku) nameParts.push(prod.sku);
        const name = nameParts.join(': ').toLowerCase();

        if (searchString && !name.includes(searchString.toLowerCase())) return;
        if (prod.mergedProductID && prod.mergedProductID.length) return;
        if (brandID && prod.brandID !== brandID) return;
        if (powerID && prod.powerID !== powerID) return;
        if (productTypeID && prod.productTypeID !== productTypeID) return;
        if (systemSizeID && prod.systemSizeID !== systemSizeID) return;
        if (prod.isFinalProduct !== isFinalProduct) return;
        if (
            productStandards &&
            productStandards.length > 0 &&
            !prod?.productStandards?.some(item => productStandards.includes(item.standardID))
        ) {
            return;
        }

        filteredProducts.push(prod);

        if (filteredProducts.length >= pageSize) return;
    });

    return filteredProducts;
};

export const clearOldProducts = async () => {
    await localForage.removeItem('med-gas-mobile_all-products');
    await localForage.removeItem('med-gas-mobile_all-products_delta');
    await localForage.removeItem('med-gas-mobile_all-products_full_sync');
};

export const clearAllProducts = async () => {
    await materialStore.clear();
    await clearOldProducts();
};

export const persistConfig: PersistorConfig = {
    keyPrefix: 'my-med-gas-mobile_',
    debounce: 300,
    storage: localForage,
    blacklist: [
        'showEditProfileModal',
        'ajaxCallsInProgress',
        'isGeneratingReportPreview',
        'toastr',
        'initialSyncActive'
    ]
};
// see https://github.com/diegoddox/react-redux-toastr/issues/249 about blacklisting toastr
const migration = createMigration.default(rootMigrations, 'appVersion');

/* eslint-disable no-constant-condition */
export default function configureStore(persistCallback: () => any) {
    const offlineConfigEdited = {
        ...offlineConfig,
        effect,
        discard,
        retry,
        detectNetwork,
        persistOptions: persistConfig,
        persistCallback
    };

    // if (process.env.NODE_ENV !== 'production') {

    const isDebug = false;
    if (isDebug) {
        // ! comment in for testing
        const composeEnhancers = require('redux-devtools-extension').composeWithDevTools(
            // for inspecting while using Edge browser remotedev.io/local, does not seem to work for mobile app
            {
                actionsBlacklist: [
                    'persist/REHYDRATE',
                    'GET_INVENTORY_SUCCESS',
                    'BEGIN_AJAX_CALL',
                    'FILTER_VISIBLE_INSTALLS',
                    'MANAGE_MEASUREMENT_POINT_LISTS_SUCCESS',
                    'Offline/BUSY',
                    'UPDATE_SYNC_STATUS_SUCCESS'
                ], // this improves the perfomance of redux devtools
                autoPause: true,
                latency: 3000,
                maxAge: 40,
                shouldHotReload: false,
                trace: true,
                stateSanitizer: (state: IinitialState) => ({
                    ...state,
                    measurementPointLists: '<<LONG_BLOB>>',
                    productInfo: '<<LONG_BLOB>>',
                    manageLocation: '<<LONG_BLOB>>',
                    facilities: '<<LONG_BLOB>>',
                    measurementPointResults: '<<LONG_BLOB>>',
                    manageJob: '<<LONG_BLOB>>',
                    // manageJob: {
                    //     ...state.manageJob,
                    //     jobsByID: '<<LONG_BLOB>>',
                    //     jobWorkOrdersByID: '<<LONG_BLOB>>'
                    // },
                    manageJobComment: '<<LONG_BLOB>>',
                    parts: '<<LONG_BLOB>',
                    simpleMeasurementPointResults: '<<LONG_BLOB>>',
                    user: '<<LONG_BLOB>',
                    photos: '<<LONG_BLOB>',
                    ts: '<<LONG_BLOB>',
                    syncStatus: '<<LONG_BLOB>>',
                    toastr: '<<LONG_BLOB>>',
                    // offline: '<<LONG_BLOB>>',
                    manageInventory: '<<LONG_BLOB>>'
                    // manageInventory: {
                    //   ...state.manageInventory,
                    //   data: '<<LONG_BLOB>>'
                    // }
                })
            }
        );
        return createStore(
            rootReducer,
            initialState,
            composeEnhancers(
                applyMiddleware(
                    thunk as ThunkMiddleware<IinitialState, AnyAction>,
                    asyncProcessingMiddleware,
                    require('redux-immutable-state-invariant').default()
                ),
                offline(offlineConfigEdited),
                migration
            )
        );
    } else {
        return createStore(
            rootReducer,
            initialState,
            compose(
                applyMiddleware(
                    thunk as ThunkMiddleware<IinitialState, AnyAction>,
                    asyncProcessingMiddleware,
                    SentryMiddleware
                ),
                offline(offlineConfigEdited),
                migration
            )
        );
    }
}
